<template>
  <div class="docs-wrap">
    <HeaderComponent />

    <div class="docs-container">
      <!-- Sidebar -->
      <aside class="docs-sidebar" :class="{ open: sidebarOpen }">
        <div class="docs-brand">
          <div class="docs-brand-title">RMS API</div>
          <div class="docs-brand-sub">
            <span class="ver">{{ spec.version }}</span>
            <span class="dot">·</span>
            <span>{{ spec.baseUrl }}</span>
          </div>
        </div>

        <nav>
          <div class="nav-section">Getting Started</div>
          <a href="#" class="nav-item" :class="{ active: selected === 'overview' }" @click.prevent="select('overview')">Overview</a>
          <a href="#" class="nav-item" :class="{ active: selected === 'authentication' }" @click.prevent="select('authentication')">Authentication</a>
          <a href="#" class="nav-item" :class="{ active: selected === 'throttling' }" @click.prevent="select('throttling')">Throttling &amp; Rate Limits</a>
          <a href="#" class="nav-item" :class="{ active: selected === 'errors' }" @click.prevent="select('errors')">Errors</a>
          <a href="#" class="nav-item" :class="{ active: selected === 'webhooks' }" @click.prevent="select('webhooks')">Webhooks</a>

          <div class="nav-section">Modules</div>
          <div v-for="mod in spec.modules" :key="mod.id" class="nav-module">
            <div class="nav-module-title">{{ mod.name }}</div>
            <a
              v-for="ep in mod.endpoints"
              :key="ep.id"
              href="#"
              class="nav-endpoint"
              :class="{ active: selected === endpointKey(mod.id, ep.id) }"
              @click.prevent="select(endpointKey(mod.id, ep.id))"
            >
              <span class="method" :class="'m-' + ep.method.toLowerCase()">{{ ep.method }}</span>
              {{ ep.name }}
              <span v-if="ep.sensitive" class="sens-badge" title="Sensitive — restricted access">SENSITIVE</span>
            </a>
          </div>
        </nav>
      </aside>

      <!-- Main pane -->
      <main class="docs-main">
        <button class="mobile-menu" @click="sidebarOpen = !sidebarOpen">☰ Menu</button>

        <!-- Overview -->
        <section v-if="selected === 'overview'" class="docs-section">
          <h1>Overview</h1>
          <p class="lede">{{ spec.overview.intro }}</p>

          <h3>Content type</h3>
          <p>{{ spec.overview.contentType }}</p>

          <h3>Response envelope</h3>
          <pre><code>{{ spec.overview.envelope }}</code></pre>

          <div class="callout">
            <strong>Base URL</strong>
            <code>{{ spec.baseUrl }}</code>
            — replace <code>{county}</code> with the target county slug (e.g. <code>meru</code>, <code>kajiado</code>, <code>mandera</code>, <code>turkana</code>, <code>homabay</code>).
          </div>

          <h3 v-if="spec.overview.sla">Service level (uptime & latency)</h3>
          <pre v-if="spec.overview.sla"><code>{{ spec.overview.sla }}</code></pre>

          <h3 v-if="spec.overview.versioning">Versioning</h3>
          <p v-if="spec.overview.versioning">{{ spec.overview.versioning }}</p>
        </section>

        <!-- Authentication -->
        <section v-else-if="selected === 'authentication'" class="docs-section">
          <h1>Authentication</h1>
          <p class="lede">{{ spec.authentication.summary }}</p>

          <h3>Sending the token</h3>
          <p>{{ spec.authentication.header }} <code>token</code>.</p>
          <pre><code>Authorization: Bearer &lt;token&gt;</code></pre>

          <h3>Token lifetime</h3>
          <p>{{ spec.authentication.tokenTtl }}</p>

          <h3>Authorization &amp; scopes</h3>
          <p>{{ spec.authentication.scopes }}</p>
        </section>

        <!-- Throttling -->
        <section v-else-if="selected === 'throttling'" class="docs-section">
          <h1>Throttling &amp; Rate Limits</h1>
          <h3>Policy</h3>
          <pre><code>{{ spec.throttling.policy }}</code></pre>
          <h3>Exceeded response</h3>
          <pre><code>{{ spec.throttling.response }}</code></pre>
        </section>

        <!-- Errors -->
        <section v-else-if="selected === 'errors'" class="docs-section">
          <h1>Errors</h1>
          <pre><code>{{ spec.errors.envelope }}</code></pre>
        </section>

        <!-- Webhooks -->
        <section v-else-if="selected === 'webhooks'" class="docs-section">
          <h1>Webhooks</h1>
          <p class="lede">{{ spec.webhooks.intro }}</p>

          <h3>Event types</h3>
          <table class="tbl">
            <thead>
              <tr><th>Event</th><th>Description</th><th>Payload shape</th></tr>
            </thead>
            <tbody>
              <tr v-for="e in spec.webhooks.events" :key="e.event">
                <td class="mono">{{ e.event }}</td>
                <td>{{ e.description }}</td>
                <td><pre class="inline"><code>{{ JSON.stringify(e.payload, null, 2) }}</code></pre></td>
              </tr>
            </tbody>
          </table>

          <h3>Signature verification</h3>
          <pre><code>{{ spec.webhooks.verification }}</code></pre>
        </section>

        <!-- Endpoint -->
        <section v-else-if="currentEndpoint" class="docs-section">
          <div class="ep-head">
            <span class="method-big" :class="'m-' + currentEndpoint.method.toLowerCase()">{{ currentEndpoint.method }}</span>
            <code class="url">{{ spec.baseUrl }}{{ currentEndpoint.path }}?function={{ currentEndpoint.function }}</code>
            <span v-if="currentEndpoint.sensitive" class="sens-badge">SENSITIVE</span>
          </div>
          <h1>{{ currentEndpoint.name }}</h1>
          <div v-if="currentEndpoint.sensitive" class="sens-banner">
            <span class="sens-icon">!</span>
            <div class="sens-body">
              <strong>Sensitive endpoint — restricted access</strong>
              {{ currentEndpoint.sensitiveNote || 'This endpoint handles live payment data and is not intended for public use. Access is limited to authorised staff and partner integrations.' }}
            </div>
          </div>
          <p class="lede">{{ currentEndpoint.summary }}</p>

          <h3>Authentication</h3>
          <p>{{ currentEndpoint.auth }}</p>

          <h3>Request</h3>
          <table class="tbl" v-if="currentEndpoint.request && currentEndpoint.request.length">
            <thead>
              <tr><th>Field</th><th>Type</th><th>Required</th><th>Notes</th></tr>
            </thead>
            <tbody>
              <tr v-for="p in currentEndpoint.request" :key="p.field">
                <td class="mono">{{ p.field }}</td>
                <td>{{ p.type }}</td>
                <td>{{ p.required ? 'Yes' : 'No' }}</td>
                <td>
                  <span v-if="p.notes">{{ p.notes }}</span>
                  <span v-if="p.example"> — e.g. <code>{{ p.example }}</code></span>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="muted">No request body — this endpoint accepts no arguments.</p>

          <h3>curl example</h3>
          <div class="code-block">
            <button class="copy-btn" @click="copyToClipboard(currentEndpoint.curl)">{{ copyLabel }}</button>
            <pre><code>{{ currentEndpoint.curl }}</code></pre>
          </div>

          <h3>Try it</h3>
          <div v-if="currentEndpoint.sensitive" class="tryit-blocked">
            Interactive testing is disabled for sensitive endpoints. Use your integration environment instead.
          </div>
          <div v-else class="tryit">
            <div class="tryit-row">
              <label>County slug</label>
              <input v-model="tryCounty" placeholder="kajiado" class="tryit-input" />
            </div>
            <div class="tryit-row">
              <label>Bearer token
                <span v-if="!currentEndpoint.auth || !currentEndpoint.auth.toLowerCase().includes('none')" class="req">*</span>
              </label>
              <input v-model="tryToken" placeholder="paste your session token" class="tryit-input" type="password" />
            </div>
            <div v-for="p in (currentEndpoint.request || [])" :key="p.field" class="tryit-row">
              <label>{{ p.field }}<span v-if="p.required" class="req">*</span></label>
              <input v-model="tryFields[p.field]" :placeholder="p.example || p.notes || p.type" class="tryit-input" />
            </div>
            <div class="tryit-actions">
              <button class="btn-send" @click="sendTry" :disabled="trySending">
                {{ trySending ? 'Sending…' : 'Send request' }}
              </button>
              <span v-if="tryStatusLabel" class="tryit-status" :class="tryStatusClass">{{ tryStatusLabel }}</span>
            </div>
            <div v-if="tryResponse" class="code-block tryit-response">
              <button class="copy-btn" @click="copyToClipboard(tryResponse)">Copy</button>
              <pre><code>{{ tryResponse }}</code></pre>
            </div>
          </div>

          <h3>Successful response</h3>
          <pre><code>{{ JSON.stringify(currentEndpoint.responseSuccess, null, 2) }}</code></pre>

          <h3 v-if="currentEndpoint.responseErrors && currentEndpoint.responseErrors.length">Common error responses</h3>
          <table class="tbl" v-if="currentEndpoint.responseErrors && currentEndpoint.responseErrors.length">
            <thead>
              <tr><th>Message</th><th>When</th></tr>
            </thead>
            <tbody>
              <tr v-for="(err, i) in currentEndpoint.responseErrors" :key="i">
                <td class="mono">{{ err.code }}</td>
                <td>{{ err.when }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- Fallback -->
        <section v-else class="docs-section">
          <p class="muted">Pick an endpoint from the sidebar.</p>
        </section>

        <!-- Footer note -->
        <footer class="docs-foot">
          Contact: <a :href="'mailto:' + spec.contact.email">{{ spec.contact.email }}</a> · {{ spec.contact.supportHours }}
        </footer>
      </main>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import HeaderComponent from "@/components/HeaderComponent.vue";
import { apiSpec } from "@/data/apiSpec";

export default {
  name: "ApiDocs",
  components: { HeaderComponent },
  data() {
    return {
      spec: apiSpec,
      selected: 'overview',
      sidebarOpen: false,
      copyLabel: 'Copy',
      tryCounty: 'kajiado',
      tryToken: '',
      tryFields: {},
      tryResponse: '',
      trySending: false,
      tryStatusLabel: '',
      tryStatusClass: '',
    };
  },
  computed: {
    currentEndpoint() {
      if (!this.selected.startsWith('ep:')) return null;
      const [, modId, epId] = this.selected.split(':');
      const mod = this.spec.modules.find(m => m.id === modId);
      if (!mod) return null;
      return mod.endpoints.find(e => e.id === epId) || null;
    },
  },
  watch: {
    currentEndpoint(ep) {
      this.tryFields = {};
      this.tryResponse = '';
      this.tryStatusLabel = '';
      this.tryStatusClass = '';
      if (ep && Array.isArray(ep.request)) {
        for (const p of ep.request) {
          if (p.field === 'function') {
            this.tryFields[p.field] = ep.function;
          } else if (p.example) {
            this.tryFields[p.field] = p.example;
          } else {
            this.tryFields[p.field] = '';
          }
        }
      }
    },
  },
  mounted() {
    if (this.$route.hash) {
      const h = this.$route.hash.replace(/^#/, '');
      if (h) this.selected = h;
    }
  },
  methods: {
    endpointKey(modId, epId) { return `ep:${modId}:${epId}`; },
    select(key) {
      this.selected = key;
      this.sidebarOpen = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      try { history.replaceState(null, '', '#' + key); } catch (_) {}
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.copyLabel = 'Copied!';
        setTimeout(() => { this.copyLabel = 'Copy'; }, 1500);
      } catch (_) {
        this.copyLabel = 'Copy failed';
        setTimeout(() => { this.copyLabel = 'Copy'; }, 1500);
      }
    },
    resolvedUrl() {
      const county = (this.tryCounty || 'kajiado').trim().toLowerCase();
      const base = this.spec.baseUrl.replace('{county}', county);
      return base + this.currentEndpoint.path;
    },
    async sendTry() {
      if (!this.currentEndpoint) return;
      this.trySending = true;
      this.tryResponse = '';
      this.tryStatusLabel = '';
      this.tryStatusClass = '';
      try {
        const form = new FormData();
        form.append('function', this.currentEndpoint.function);
        for (const [k, v] of Object.entries(this.tryFields)) {
          if (k === 'function') continue;
          if (v !== undefined && v !== null && v !== '') form.append(k, v);
        }
        const headers = {};
        if (this.tryToken && this.tryToken.trim()) {
          headers['Authorization'] = 'Bearer ' + this.tryToken.trim();
        }
        const res = await fetch(this.resolvedUrl(), { method: 'POST', body: form, headers });
        const text = await res.text();
        let pretty = text;
        try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch (_) {}
        this.tryResponse = pretty;
        this.tryStatusLabel = `HTTP ${res.status}`;
        this.tryStatusClass = res.ok ? 'ok' : 'bad';
      } catch (e) {
        this.tryResponse = String(e && e.message ? e.message : e);
        this.tryStatusLabel = 'Request failed (network / CORS)';
        this.tryStatusClass = 'bad';
      } finally {
        this.trySending = false;
      }
    },
  },
};
</script>

<style scoped>
.docs-wrap { min-height: 100vh; background: #f6f8fa; }
.docs-container {
  display: flex;
  max-width: 1400px;
  margin: 0 auto;
  gap: 24px;
  padding: 92px 20px 20px;
}

/* Sidebar */
.docs-sidebar {
  flex: 0 0 280px;
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,.04);
  align-self: flex-start;
  position: sticky;
  top: 92px;
  max-height: calc(100vh - 112px);
  overflow-y: auto;
}
.docs-brand { padding-bottom: 12px; border-bottom: 1px solid #eaeef2; margin-bottom: 12px; }
.docs-brand-title { font-size: 16px; font-weight: 700; color: #1f2328; }
.docs-brand-sub {
  font-size: 11px; color: #6e7781; margin-top: 4px;
  display: flex; gap: 6px; align-items: center;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
}
.docs-brand-sub .ver {
  background: #ddf4ff; color: #0969da; padding: 2px 6px; border-radius: 4px;
  font-weight: 700;
}
.docs-brand-sub .dot { color: #8c959f; }
.nav-section {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.6px;
  color: #6e7781; font-weight: 700;
  margin: 16px 0 6px;
}
.nav-item, .nav-endpoint {
  display: block; padding: 6px 8px; border-radius: 5px;
  font-size: 13px; color: #24292f; text-decoration: none;
  margin: 1px 0;
}
.nav-item:hover, .nav-endpoint:hover { background: #f1f4f9; }
.nav-item.active, .nav-endpoint.active { background: #1f6feb; color: #fff; }
.nav-item.active .method, .nav-endpoint.active .method { color: #fff; }
.nav-module { margin-bottom: 10px; }
.nav-module-title {
  font-size: 11px; font-weight: 700; color: #57606a;
  margin: 8px 4px 2px;
}
.method {
  display: inline-block; font-size: 9px; font-weight: 700;
  padding: 1px 4px; border-radius: 3px; margin-right: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  min-width: 32px; text-align: center;
}
.m-get    { background: #dbeafe; color: #1e40af; }
.m-post   { background: #dcfce7; color: #14532d; }
.m-put    { background: #fef3c7; color: #78350f; }
.m-delete { background: #fee2e2; color: #991b1b; }

/* Main */
.docs-main {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 10px;
  padding: 32px 40px;
  box-shadow: 0 2px 8px rgba(0,0,0,.04);
}
.mobile-menu {
  display: none;
  padding: 8px 12px; border: 1px solid #d0d7de; border-radius: 6px;
  background: #fff; cursor: pointer; margin-bottom: 16px; font-size: 13px;
}
.docs-section h1 {
  font-size: 26px; color: #1f2328; margin: 0 0 8px;
}
.docs-section h3 {
  font-size: 15px; color: #1f2328; margin: 24px 0 8px;
  text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700;
}
.docs-section p { color: #24292f; line-height: 1.6; font-size: 14px; }
.docs-section .lede { font-size: 15px; color: #57606a; }
.docs-section .muted { color: #6e7781; font-size: 13px; }

.ep-head {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; background: #f6f8fa; border-radius: 8px;
  margin-bottom: 16px;
  overflow-x: auto;
}
.method-big {
  display: inline-block; padding: 4px 8px; border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  font-weight: 700; font-size: 12px;
}
.ep-head .url {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  font-size: 12px; color: #24292f; white-space: nowrap;
}

/* Tables */
.tbl { width: 100%; border-collapse: collapse; margin: 8px 0 16px; font-size: 13px; }
.tbl th {
  text-align: left; padding: 8px 10px;
  background: #f6f8fa; border-bottom: 2px solid #d0d7de;
  color: #57606a; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;
}
.tbl td { padding: 10px; border-bottom: 1px solid #eaeef2; vertical-align: top; }
.mono, code { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace; }
code {
  background: #f6f8fa; padding: 1px 6px; border-radius: 4px;
  font-size: 12px; color: #cf222e;
}
pre {
  background: #0d1117; color: #e6edf3;
  padding: 14px 16px; border-radius: 8px;
  overflow-x: auto; font-size: 12px; line-height: 1.55;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  white-space: pre-wrap; word-break: break-word;
}
pre code { background: transparent; color: inherit; padding: 0; }
pre.inline { max-width: 320px; }

.code-block { position: relative; }
.copy-btn {
  position: absolute; top: 8px; right: 8px;
  background: #24292f; color: #fff; border: 1px solid #444c56;
  padding: 4px 10px; border-radius: 4px;
  font-size: 11px; font-weight: 600; cursor: pointer;
  z-index: 2;
}
.copy-btn:hover { background: #444c56; }

.callout {
  background: #ddf4ff; border-left: 4px solid #0969da;
  padding: 12px 14px; border-radius: 6px; margin: 16px 0;
  font-size: 13px;
}
.callout strong { display: block; color: #0969da; margin-bottom: 4px; }

.docs-foot {
  margin-top: 40px; padding-top: 20px;
  border-top: 1px solid #eaeef2;
  color: #6e7781; font-size: 12px;
}
.docs-foot a { color: #1f6feb; text-decoration: none; }

.tryit { border: 1px solid #d0d7de; border-radius: 8px; padding: 14px 16px; margin: 8px 0 16px; background: #fbfcfd; }
.tryit-row { display: flex; align-items: center; gap: 12px; margin: 6px 0; }
.tryit-row label { flex: 0 0 160px; font-size: 12px; color: #57606a; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace; }
.tryit-row .req { color: #cf222e; margin-left: 2px; }
.tryit-input {
  flex: 1; padding: 6px 10px;
  border: 1px solid #d0d7de; border-radius: 5px;
  font-size: 13px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  background: #fff; color: #24292f;
}
.tryit-input:focus { outline: none; border-color: #1f6feb; box-shadow: 0 0 0 3px rgba(31,111,235,.2); }
.tryit-actions { display: flex; align-items: center; gap: 12px; margin-top: 10px; }
.btn-send {
  background: #1f6feb; color: #fff; border: none;
  padding: 8px 18px; border-radius: 6px; font-size: 13px; font-weight: 600;
  cursor: pointer;
}
.btn-send:hover:not(:disabled) { background: #1856b0; }
.btn-send:disabled { opacity: 0.6; cursor: not-allowed; }
.tryit-status { font-size: 12px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace; padding: 3px 8px; border-radius: 4px; }
.tryit-status.ok { background: #dcfce7; color: #14532d; }
.tryit-status.bad { background: #fee2e2; color: #991b1b; }
.tryit-response { margin-top: 12px; }
.tryit-blocked {
  background: #fef2f2; color: #7f1d1d;
  border-left: 4px solid #dc2626;
  padding: 12px 14px; border-radius: 6px; font-size: 13px;
  margin: 8px 0 16px;
}

.sens-badge {
  display: inline-block;
  background: #fee2e2; color: #991b1b;
  font-size: 9px; font-weight: 800;
  padding: 1px 5px; border-radius: 3px;
  margin-left: 6px; letter-spacing: 0.4px;
  vertical-align: middle;
}
.nav-endpoint.active .sens-badge { background: #fecaca; color: #7f1d1d; }

.sens-banner {
  display: flex; gap: 12px; align-items: flex-start;
  background: #fef2f2; border-left: 4px solid #dc2626;
  padding: 14px 16px; border-radius: 6px; margin: 0 0 20px;
}
.sens-banner .sens-icon {
  flex: 0 0 auto; width: 24px; height: 24px;
  background: #dc2626; color: #fff; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 14px; line-height: 1;
}
.sens-banner .sens-body { color: #7f1d1d; font-size: 13px; line-height: 1.55; }
.sens-banner .sens-body strong { display: block; font-size: 12px; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 4px; color: #991b1b; }

/* Mobile */
@media (max-width: 900px) {
  .docs-container { flex-direction: column; padding: 84px 12px 12px; }
  .docs-sidebar {
    position: fixed; left: -320px; top: 0; height: 100vh;
    max-height: 100vh; z-index: 100; transition: left 0.2s ease;
    border-radius: 0; width: 260px;
  }
  .docs-sidebar.open { left: 0; }
  .docs-main { padding: 20px 16px; }
  .mobile-menu { display: block; }
}
</style>
